import React, { useEffect, useRef } from 'react';
import Card from 'ui/views/cards/Card';
import styles from './styles.scss';
import cx from 'classnames';
import ChevronRightIcon from 'ui/elements/icons/ChevronRightIcon';
import { Link } from 'react-router-dom';
import { communityUrls, companyUrls } from 'urls';
import { ContentAPIUrls } from 'apis/CompanyAPI/content/UpdateAPI';
import Resource from 'util/resource/Resource';
import useResource from 'util/resource/useResource';
import { EmbeddedUpdateDTO } from 'types/content/embeddedUpdate';
import { asCompanyAuthor, asUserAuthor, AuthorImage } from 'domain/shared/author';
import { timeSince } from 'util/dateUtils';
import { richTextContentPreviewParser } from 'domain/Updates/shared/utils';
import { POST_BLOT_CLASS } from 'ui/elements/form/RichTextEditor/components/UpdateBlot';
import HtmlContentWithMentions from 'domain/shared/HtmlContentWithMentions';

const MAX_NESTING_LEVEL = 3;

export function isNestingTooDeep(element: HTMLElement): boolean {
  const nestingLevel = countParentPostBlots(element);
  return nestingLevel === -1;
}

export function countParentPostBlots(element: HTMLElement): number {
  const hasPostBlotClass = (el: HTMLElement | null): boolean => el?.classList.contains(POST_BLOT_CLASS) ?? false;

  const countInAncestors = (current: HTMLElement | null, count: number): number => {
    if (!current || count > MAX_NESTING_LEVEL) return count > MAX_NESTING_LEVEL ? -1 : count;
    return countInAncestors(current.parentElement, hasPostBlotClass(current) ? count + 1 : count);
  };

  return countInAncestors(element, 0);
}

interface Props {
  updateId: UUID;
  className?: string;
  onContentLoaded?: () => void;
}

export default function ShareUpdateCard({ updateId, className, onContentLoaded }: Props) {
  const { resource: updateResource } = useResource<EmbeddedUpdateDTO>(ContentAPIUrls.getUpdateForEmbed(updateId));
  const cardRef = useRef<HTMLDivElement>(null);
  const [isTooDeeplyNested, setIsTooDeeplyNested] = React.useState(false);

  useEffect(() => {
    if (cardRef.current) {
      setIsTooDeeplyNested(isNestingTooDeep(cardRef.current));
    }
  }, []);

  useEffect(() => {
    if (updateResource.state === 'fetched' && onContentLoaded) onContentLoaded();
  }, [updateResource.state]);

  if (isTooDeeplyNested) {
    return (
      <Card elevation={2} style={{ borderRadius: 4, marginBottom: 16 }}>
        <div className="text-grey text italic">This content is unavailable</div>
      </Card>
    );
  }

  return (
    <Resource
      resource={updateResource}
      renderError={() => <div className="text-grey text italic">This content is unavailable</div>}
      renderLoading={() => (
        <div className="u-fade-in" style={{ height: 100, borderRadius: 4, backgroundColor: '#E5E5E5' }} />
      )}
    >
      {(update: EmbeddedUpdateDTO) => {
        const author = update.postedAs
          ? asCompanyAuthor(
              { ...update.author, position: update.author.title },
              { ...update.postedAs, logoURL: update.postedAs.logoUrl },
            )
          : asUserAuthor({ ...update.author, position: update.author.title });

        const getUrl = () => {
          if (update.location.type === 'company') {
            return companyUrls.update.view(update.location.id.toString(), updateId);
          }
          if (update.location.type === 'community') {
            return communityUrls.update.view(update.location.id.toString(), updateId);
          }
          return '#';
        };

        return (
          <Card
            ref={cardRef}
            className={cx(styles.shareCard, styles.updateCard, className)}
            elevation={2}
            padding="none"
          >
            <Link to={getUrl()} target="_blank" rel="noopener noreferrer">
              <div className="u-flex u-flex-space-between u-half-padding-x u-half-padding-y">
                <div className="u-flex">
                  <div>
                    <AuthorImage size={40} author={author} />
                  </div>
                  <div className="u-flex u-flex--column u-half-spacing-left">
                    <span className="text-weight-bold text-small">
                      Update by {author?.type === 'company' ? author.user?.name : author?.name}
                    </span>
                    <span className="text-metadata">{timeSince(update.createdAt)}</span>
                    {update.content && (
                      <div
                        style={{
                          maxHeight: 200,
                          overflow: 'hidden',
                          pointerEvents: 'none',
                        }}
                      >
                        <HtmlContentWithMentions
                          className={cx('ql-rich-text', 'data-content-read-more', {
                            'text-grey text-italic': !update.content,
                          })}
                        >
                          {richTextContentPreviewParser(update.content, 100, 0).previewHtmlString}
                        </HtmlContentWithMentions>
                      </div>
                    )}
                  </div>
                </div>
                <ChevronRightIcon color="dark-grey" fontSize="small" strokeWidth={2} />
              </div>
            </Link>
          </Card>
        );
      }}
    </Resource>
  );
}
